/* eslint-disable max-lines */
import {
  ClearOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  FireOutlined,
  PlusOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { CopyViewDocument, DeleteCommitmentKpiLabelDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import { canEditCommitmentKpiLabel, ErrorContext, useCanForView, useMe } from '@salescore/client-common'
import type { ViewConfigKpiPivot, ViewConfigKpiPreset } from '@salescore/core'
import { useOnClickOutside } from '@salescore/frontend-common'
import { App, Button, Popconfirm } from 'antd'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type CSSProperties, type ReactElement, useContext, useEffect, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { kpiParameterSchema } from '../../../components/view_ui/ViewUIKpiPivot/CommitmentKpiLabelForm'
import { buildCommitmentKpiLabelModalProperties } from '../../../components/view_ui/ViewUIKpiPivot/CommitmentKpiLabelModal'
import { buildKpiBulkDuplicateModalProperties } from '../../../components/view_ui/ViewUIKpiPivot/KpiBulkDuplicateModal'
import { buildKpiPivotKpisFormModalProperties } from '../../../components/view_ui/ViewUIKpiPivot/KpiPivotKpisFormModal'
import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { useNavigationModal } from '../../../recoil/navigation/hooks'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { useViewConfigKpiPivot } from '../../../recoil/view/hooks/useViewConfigKpiPivot'
import { RSheetsStyle } from '../../util/RSheetsStyle'

export interface ContextMenuItem {
  text: string | ReactElement
  icon: ReactElement
  disabled?: boolean
  className?: string
  onClick?: () => void
  confirmMessage?: string | ReactElement
  tooltipMessage?: string
}

interface ContextMenuProperties {
  menu: ContextMenuItem[]
  style: CSSProperties
  hideContextMenu: () => void
}

const baseStyle = {
  backgroundColor: 'white',
  boxShadow: '0 2px 6px 2px rgb(60 64 67 / 15%)',
  borderRadius: 2,
  zIndex: RSheetsStyle.zIndex.contextMenu,
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function ContextMenu({ menu, style, hideContextMenu }: ContextMenuProperties) {
  const reference = useRef<HTMLDivElement>(null)
  // Popconfirm は渡された data 属性を popup の DOM に転送しないため antd のクラスで除外する
  useOnClickOutside(reference, () => {
    hideContextMenu()
  }, ['anticon', 'ant-popconfirm'])
  useEffect(() => {
    if (reference.current !== null) {
      reference.current.focus()
    }
  }, [])

  return (
    <div
      ref={reference}
      style={{
        ...baseStyle,
        ...style,
      }}
    >
      {menu.map((item, index) =>
        item.confirmMessage === undefined ? (
          <Button
            key={`context-menu-${index}-button`}
            type="text"
            style={{ width: '100%', textAlign: 'left' }}
            icon={item.icon}
            disabled={item.disabled}
            onClick={item.onClick}
            className={`${item.className ?? ''} justify-start`}
          >
            {item.text}
          </Button>
        ) : (
          <Popconfirm
            key={`context-menu-${index}-popconfirm`}
            title={item.confirmMessage}
            onConfirm={item.onClick}
            disabled={item.disabled}
            okText={t(`はい`)}
            cancelText={t(`いいえ`)}
          >
            <Button
              type="text"
              style={{ width: '100%', textAlign: 'left' }}
              icon={item.icon}
              disabled={item.disabled}
              className={`${item.className ?? ''} justify-start`}
            >
              {item.text}
            </Button>
          </Popconfirm>
        ),
      )}
    </div>
  )
}

interface KpiContextMenuProperties {
  kpiId: string | undefined
  hideContextMenu: () => void
  setAddKpiDropdownVisible: (visibility: boolean) => void
  addKpiDirection: 'right' | 'down'
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}

export const useKpiContextMenu = ({
  kpiId,
  hideContextMenu,
  setAddKpiDropdownVisible,
  addKpiDirection,
  view,
  setHasChangeToViewSchema,
}: KpiContextMenuProperties) => {
  const { drillDownModal } = useNavigationModal()
  const [kpiParameter, setKpiParameter] = useRecoilState(kpiParameterAtom)
  const { modal } = App.useApp()
  const canUpdate = useCanForView('update', view)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const canCreateKpi = useCanForView('create', { type: 'kpi' })
  const [copy] = useMutation(CopyViewDocument)
  const [deleteCommitmentKpiLabelMutation] = useMutation(DeleteCommitmentKpiLabelDocument)
  const [config, setConfig] = useViewConfigKpiPivot()
  const context = useRecoilValue(viewsContextAtom)
  const { message } = App.useApp()
  const me = useMe()
  const isCommitmentKpiLabeldKpi = kpiParameter.goalConfig?.commitmentKpiLabel?.viewId === kpiId
  const errorContext = useContext(ErrorContext)

  if (kpiId === undefined) {
    return []
  }

  const addKpi = async (previousKpiId: string, kpiViewId: string) => {
    const oldKpis = config?.kpis ?? []
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const previousKpiIndex = Math.max(oldKpis.findIndex((x) => x.viewId === previousKpiId) ?? -1, -1) + 1
    const previousKpi = oldKpis[previousKpiIndex]
    const newKpi: ViewConfigKpiPivot['kpis'][0] = {
      viewId: kpiViewId,
      kpiGroupName: previousKpi?.kpiGroupName,
    }
    const kpis = Array.dedupeWith(
      [...oldKpis.slice(0, previousKpiIndex), newKpi, ...oldKpis.slice(previousKpiIndex)],
      (a, b) => a.viewId === b.viewId,
    )
    try {
      await context.onAddResource({ viewIds: [kpiViewId] })

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const newConfig = { ...config!, kpis }
      setConfig(newConfig)

      // WORKAROUND:
      // configの変更の保存ロジックは、変更後にhasChangeToViewSchemaAtomをtrueにすることで、
      // それをトリガーにuseEffectが呼ばれてその中でupdateViewを実行するのが一般的。
      // （ここのロジックはuseWatchを参照）
      // しかし、KPIを新規作成するとき、別タブが開いてしまう影響でこのuseEffectが呼ばれず、updateViewが実行されない。
      // そのため、ここで明示的にupdateViewを行う
      // 権限がない場合はここまで辿り着かず、またサーバーサイドでエラーになる想定
      await context.updateView({
        id: view.id,
        config: newConfig,
      })
    } catch {
      message.error(t(`エラーが発生しました`))
    }
  }

  return [
    {
      icon: <EditOutlined />,
      onClick: () => {
        drillDownModal.showModal({
          viewId: kpiId,
          type: `any`,
        })
        hideContextMenu()
      },
      text: t(`KPI設定`),
      disabled: !canDisplayConfigEdit,
    },
    {
      icon: <PlusOutlined />,
      onClick: () => {
        setAddKpiDropdownVisible(true)
      },
      text: addKpiDirection === 'right' ? t(`右にKPIを追加`) : t(`下にKPIを追加`),
      disabled: !canUpdate || !canDisplayConfigEdit,
    },
    {
      icon: <EyeInvisibleOutlined />,
      onClick: () => {
        setKpiParameter((oldValue) => {
          const kpiIds = new Set(config?.kpis.map((x) => x.viewId))
          return {
            ...oldValue,
            invisibleKpiIds: Array.dedupe([...(oldValue.invisibleKpiIds ?? []), kpiId]).filter((id) => kpiIds.has(id)), // 削除されているkpiがinvisibleに残り続けることがあるので、ここで除去してしまう
          }
        })
        hideContextMenu()
      },
      text: t(`KPIを非表示`),
      disabled: !canDisplayConfigEdit,
    },
    {
      icon: <CopyOutlined />,
      onClick: async () => {
        try {
          const { data: result } = await copy({
            variables: {
              organizationId: getOrganizationIdFromPath(),
              id: kpiId,
            },
          })
          const savedView = result?.copyView
          if (savedView === undefined) {
            message.warning(t(`エラーが発生しました`))
            return
          }

          message.success(t(`「{{name}}」を作成しました。`, { name: savedView.name }))
          await context.refetchViews()
          await addKpi(kpiId, savedView.id)
          hideContextMenu()
        } catch (error) {
          if (error instanceof Error) {
            message.error(`${t(`エラーが発生しました。`)}${error.message}`)
          } else {
            message.error(t(`エラーが発生しました`))
          }
        }
      },
      text: t(`KPIを複製`),
      disabled: !canCreateKpi || !canDisplayConfigEdit,
    },
    {
      icon: <CopyOutlined />,
      onClick: () => {
        const m = modal.confirm(
          buildKpiBulkDuplicateModalProperties({
            onFinish: () => {
              m.destroy()
            },
            view,
          }),
        )
      },
      text: t(`KPIを一括複製`),
      disabled: !canCreateKpi || !canDisplayConfigEdit,
    },
    {
      icon: <FireOutlined />,
      onClick: async () => {
        if (isCommitmentKpiLabeldKpi) {
          const kpiParameterValue = kpiParameterSchema.parse(kpiParameter)
          await deleteCommitmentKpiLabelMutation({
            variables: {
              organizationId: getOrganizationIdFromPath(),
              goalConfigId: kpiParameterValue.goalConfig.id,
            },
            onCompleted: () => {
              void message.info(t(`魂KPIを解除しました`))
              setKpiParameter({
                ...kpiParameter,
                goalConfig: isNotNullish(kpiParameter.goalConfig)
                  ? {
                      ...kpiParameter.goalConfig,
                      commitmentKpiLabel: undefined,
                    }
                  : undefined,
              })
            },
            onError: (error) => {
              errorContext.throwErrors(error)
            },
          })
        } else {
          const m = modal.confirm(
            buildCommitmentKpiLabelModalProperties({
              kpiViewId: kpiId,
              onFinish: () => {
                m.destroy()
              },
              view,
            }),
          )
        }
        hideContextMenu()
      },
      text: isCommitmentKpiLabeldKpi ? t(`魂KPIを解除`) : t(`魂KPIとして設定`),
      disabled: (() => {
        // eslint-disable-next-line eqeqeq
        if (kpiParameter.goalConfig == null || !canEditCommitmentKpiLabel(me)) {
          return true
        }
        if (isCommitmentKpiLabeldKpi) {
          return false
        }
        // 時間軸が月時ではない、またはユーザー軸がユーザーまたはグループではない場合は設定不可
        if (
          kpiParameter.goalConfig.timeSpanType !== 'month' ||
          !(kpiParameter.goalConfig.userType === 'user' || kpiParameter.goalConfig.userType === 'group')
        ) {
          return true
        }
        // ゼロ目標は設定不可
        const kpiInfo = kpiParameter.goalConfig.kpiViews.find((kpi) => kpi.id === kpiId)
        if (kpiInfo?.achievementStatusTargetValueType === 'zero') {
          return true
        }
        // 魂KPIが設定されているKPIが既にある場合は設定不可
        const isAlreadyUsedAsCommitmentKpiLabelKpi = config?.kpis.some(
          (kpi) => kpiParameter.goalConfig?.commitmentKpiLabel?.viewId === kpi.viewId,
        )
        return isAlreadyUsedAsCommitmentKpiLabelKpi
      })(),
      confirmMessage: isCommitmentKpiLabeldKpi ? t(`本当に解除しますか？`) : undefined,
    },
    {
      icon: <DeleteOutlined />,

      onClick: (): void => {
        const kpiToDelete = config?.kpis.find((x) => x.viewId === kpiId)
        if (config === undefined || kpiToDelete === undefined) {
          return
        }
        const siblingKpis = config.kpis.filter((x) => x.kpiGroupName === kpiToDelete.kpiGroupName)
        const newPresets = config.presets?.map((preset) => updatePresetForKpiDeletion(preset, kpiToDelete.viewId))

        setConfig({
          ...config,
          kpis: config.kpis
            .map((x) => {
              // KPI削除後にKPIグループが空になる場合、グループが残るようにviewIdに重複しない仮の値を入れる
              if (x.viewId === kpiToDelete.viewId) {
                return siblingKpis.length > 1
                  ? undefined
                  : {
                      viewId: `${view.id}-${kpiToDelete.kpiGroupName}-noneKpiGroup`,
                      kpiGroupName: kpiToDelete.kpiGroupName,
                    }
              }
              return x
            })
            .filter(isNotNullish),
          presets: newPresets,
        })
        setKpiParameter({
          ...kpiParameter,
          sorter: {
            columnKeys: undefined,
          },
        })
        setHasChangeToViewSchema(true)
        hideContextMenu()
      },
      text: t(`KPIを削除`),
      disabled: !canUpdate || !canDisplayConfigEdit,
      className: `text-red-500`,
      confirmMessage: t(`本当に削除しますか？`),
    },
  ]
}

const updatePresetForKpiDeletion = (preset: ViewConfigKpiPreset, kpiIdToDelete: string): ViewConfigKpiPreset => ({
  ...preset,
  parameter: {
    ...preset.parameter,
    invisibleKpiIds: preset.parameter.invisibleKpiIds?.filter((id) => id !== kpiIdToDelete),
    sorter: (preset.parameter.sorter?.columnKeys ?? []).some((columnKey) => columnKey.key.includes(kpiIdToDelete))
      ? {}
      : preset.parameter.sorter,
  },
})

export const useHeaderColumnContextMenu = ({
  column,
  hideContextMenu,
  setHasChangeToViewSchema,
}: {
  column: RSheetColumn<RSheetRecordNode>
  hideContextMenu: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}) => {
  const [kpiParameter, setKpiParameter] = useRecoilState(kpiParameterAtom)
  const [config, setConfig] = useViewConfigKpiPivot()

  function setKpiParameterOrder(key: string, order: 'asc' | 'desc') {
    setKpiParameter({
      ...kpiParameter,
      sorter: {
        columnKeys: [{ key, order }],
      },
    })
    setHasChangeToViewSchema(true)
    hideContextMenu()
  }

  const { field } = column
  if (field === undefined) {
    return []
  }
  return [
    column.sortState === 'asc'
      ? undefined
      : {
          icon: <SortAscendingOutlined />,
          onClick: () => {
            setKpiParameterOrder(field.name, 'asc')
          },
          text: t(`昇順で並び替え`),
        },
    column.sortState === 'desc'
      ? undefined
      : {
          icon: <SortDescendingOutlined />,
          onClick: () => {
            setKpiParameterOrder(field.name, 'desc')
          },
          text: t(`降順で並び替え`),
        },
    column.sortState === undefined
      ? undefined
      : {
          icon: <ClearOutlined />,
          onClick: () => {
            setKpiParameter({
              ...kpiParameter,
              sorter: {
                columnKeys: undefined,
              },
            })
            setHasChangeToViewSchema(true)
            hideContextMenu()
          },
          text: t(`並び替えを解除`),
        },
    {
      icon: <ClearOutlined />,
      onClick: () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setConfig({ ...config!, ui: { ...config!.ui, appearance: undefined } })
        setHasChangeToViewSchema(true)
        hideContextMenu()
      },
      text: t(`列幅設定のリセット`),
    },
  ].filter(isNotNullish)
}

export const useHeaderRowContextMenu = ({
  setDimensionFilterDropdownVisible,
  setDimensionSorterDropdownVisible,
  setAddRowDimensionDropdownVisible,
  hideContextMenu,
  rowIndex,
  column,
  columns,
  view,
  setHasChangeToViewSchema,
}: {
  setDimensionFilterDropdownVisible: (visibility: boolean) => void
  setDimensionSorterDropdownVisible: (visibility: boolean) => void
  setAddRowDimensionDropdownVisible: (visibility: boolean) => void
  hideContextMenu: () => void
  rowIndex: number
  column: RSheetColumn<RSheetRecordNode>
  columns: Array<RSheetColumn<RSheetRecordNode>>
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}) => {
  const { modal } = App.useApp()
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)

  return [
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: 行のフィルタと列のフィルタを分ける
    column.type === 'rowKpi'
      ? undefined
      : {
          text: t(`フィルタ設定`),
          icon: <FilterOutlined />,
          disabled: !canDisplayConfigEdit,
          onClick: () => {
            setDimensionFilterDropdownVisible(true)
            setDimensionSorterDropdownVisible(false)
            setAddRowDimensionDropdownVisible(false)
          },
        },
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: ここから削除できるようにする
    // column.isFiltered === true
    //   ? {
    //       text: `フィルタの削除`,
    //       icon: <ClearOutlined />,
    //       onClick: () => {
    //         // filterMutations.removeFilterByField({ field })
    //         hide()
    //       },
    //     }
    //   : undefined,
    column.type === 'rowKpi'
      ? undefined
      : {
          text: t(`手動で並び替え`),
          icon: <SortAscendingOutlined />,
          onClick: () => {
            setDimensionSorterDropdownVisible(true)
            setDimensionFilterDropdownVisible(false)
            setAddRowDimensionDropdownVisible(false)
          },
        },
    column.type === 'rowKpi'
      ? {
          text: t(`KPIグループ設定`),
          icon: <SettingOutlined />,
          disabled: !canDisplayConfigEdit,
          onClick: () => {
            const m = modal.confirm(
              buildKpiPivotKpisFormModalProperties({
                onFinish: () => {
                  m.destroy()
                },
                columns,
                view,
                setHasChangeToViewSchema,
              }),
            )
          },
        }
      : undefined,
    {
      text: t(`分析軸を追加`),
      icon: <PlusOutlined />,
      disabled: !canDisplayConfigEdit,
      onClick: () => {
        setAddRowDimensionDropdownVisible(true)
        setDimensionFilterDropdownVisible(false)
        setDimensionSorterDropdownVisible(false)
      },
    },
    {
      text: t(`分析軸を削除`),
      icon: <DeleteOutlined />,
      disabled: !canDisplayConfigEdit,
      className: `text-red-500`,
      onClick: () => {
        setKpiPivotParameter({
          ...kpiPivotParameter,
          pivot: {
            ...kpiPivotParameter.pivot,
            rows: [
              ...kpiPivotParameter.pivot.rows.slice(0, rowIndex),
              ...kpiPivotParameter.pivot.rows.slice(rowIndex + 1),
            ],
          },
        })
        // setHasChangeToViewSchema(true) // configの中で現在の軸設定は保存していないので、ここでhasChangeを呼ぶ必要はない。
        hideContextMenu()
      },
    },
  ].filter(isNotNullish)
}
